import { AdditionalPicksStages, PortalCarouselStages } from "../config"
import type { BotTag } from "../utils/bot-tags"
import {
  computeSynergies,
  getSynergyTier
} from "../models/colyseus-models/synergies"
import PokemonFactory from "../models/pokemon-factory"
import { getPokemonData } from "../models/precomputed/precomputed-pokemon-data"
import { Rarity } from "../types/enum/Game"
import {
  CraftableItems,
  Item,
  ItemComponentsNoScarf,
  Scarves,
  Tools
} from "../types/enum/Item"
import { Passive } from "../types/enum/Passive"
import {
  NonPkm,
  Pkm,
  PkmDuos,
  PkmFamily,
  PkmIndex
} from "../types/enum/Pokemon"
import { Synergy } from "../types/enum/Synergy"
import type { IBot, IDetailledPokemon, IStep } from "../types/models/bot-v2"
import { isIn } from "../utils/array"
import { logger } from "../utils/logger"
import { clamp, min } from "../utils/number"

export const MAX_BOTS_STAGE = 30

export const DEFAULT_BOT_STATE = {
  steps: Array.from({ length: MAX_BOTS_STAGE + 1 }, () => ({
    roundsRequired: 1,
    board: []
  })) as IStep[],
  avatar: PkmIndex[Pkm.DITTO] + "/Normal",
  author: "",
  elo: 1200,
  name: Pkm.DITTO,
  id: "",
  approved: false,
  tags: [] as BotTag[]
}

export const POWER_SCORE_BY_CATEGORY = {
  "COMMON T1": 1,
  "COMMON 2S T2": 2.5,
  "COMMON 2S T3": 3.5,
  "COMMON 3S T2": 2,
  "COMMON 3S T3": 4,
  "COMMON 3S T4": 5,
  "UNCOMMON T1": 1.25,
  "UNCOMMON 2S T2": 3,
  "UNCOMMON 2S T3": 4,
  "UNCOMMON 3S T2": 2.5,
  "UNCOMMON 3S T3": 5,
  "UNCOMMON 3S T4": 7,
  "RARE T1": 1.5,
  "RARE 2S T2": 4,
  "RARE 2S T3": 7,
  "RARE 3S T2": 3,
  "RARE 3S T3": 6,
  "HATCH T1": 1.5,
  "HATCH 3S T2": 3,
  "HATCH 3S T3": 5,
  "EPIC T1": 2,
  "EPIC 2S T2": 5,
  "EPIC 2S T3": 7,
  "EPIC 3S T2": 4,
  "EPIC 3S T3": 8,
  "EPIC 2S T2 DUO": 4,
  "ULTRA T1": 3,
  "ULTRA 3S T2": 6,
  "ULTRA 3S T3": 10,
  "UNIQUE T1": 2,
  "UNIQUE T2": 3,
  "UNIQUE T3": 4,
  "UNIQUE T4": 5,
  "UNIQUE T3 DUO": 3,
  "LEGENDARY T2": 5,
  "LEGENDARY T3": 6,
  "LEGENDARY T4": 8,
  "LEGENDARY DUO": 4,
  "SPECIAL T1": 1.5,
  "SPECIAL T2": 3,
  "SPECIAL T3": 5,
  "SPECIAL T4": 7
}

export const POWER_AVERAGES = [
  2, 2, 3, 4, 4.5, 5, 6, 7, 8, 9, 11, 11.5, 12, 13, 14, 15, 16, 17, 18, 19, 24,
  25, 25.5, 26, 27, 27.5, 28, 28.5, 29, 29.5, 30, 30.5, 31, 31.5, 32, 32.5, 33,
  33.5, 34, 34.5, 35, 35.5, 36, 36.5, 37, 37.5, 38, 38.5, 39, 39.5, 40
]

export const BOT_SCORES = {
  INCOMPLETE: { label: "Incomplete", color: "#999" },
  VERY_EASY: { label: "Very Easy", color: "#109fff" },
  EASY: { label: "Easy", color: "#92cc41" },
  MEDIUM: { label: "Medium", color: "yellow" },
  HARD: { label: "Hard", color: "#f7d51d" },
  VERY_HARD: { label: "Very Hard", color: "#e76e55" },
  ILLEGAL: { label: "Illegal", color: "#761c1e" }
}

export function getBotScore(value: number) {
  let score = BOT_SCORES.INCOMPLETE
  if (value < 10) score = BOT_SCORES.INCOMPLETE
  else if (value < 26) score = BOT_SCORES.VERY_EASY
  else if (value < 42) score = BOT_SCORES.EASY
  else if (value < 58) score = BOT_SCORES.MEDIUM
  else if (value < 74) score = BOT_SCORES.HARD
  else if (value < 90) score = BOT_SCORES.VERY_HARD
  else score = BOT_SCORES.ILLEGAL
  return score
}

export function getCategory(pkm: Pkm): string {
  const p = getPokemonData(pkm)
  let category = p.rarity.toUpperCase()
  if (
    [Rarity.UNIQUE, Rarity.LEGENDARY, Rarity.SPECIAL].includes(p.rarity) ===
      false &&
    p.stars > 1
  ) {
    category += p.stages === 2 ? " 2S" : " 3S"
  }
  category += ` T${p.stars}`
  if (Object.values(PkmDuos).some((duo) => duo.includes(pkm))) {
    category += " DUO"
  }
  return category
}

Object.values(Pkm).forEach((pkm) => {
  const c = getCategory(pkm)
  if (c in POWER_SCORE_BY_CATEGORY === false) {
    logger.warn(`Couldn't find power category for ${pkm}: ${c}`)
  }
})

export function getPowerScore(board: IDetailledPokemon[]): number {
  return board
    .filter((p) => p.y > 0)
    .reduce((sum, pkm) => sum + getUnitPowerScore(pkm.name), 0)
}

export function getUnitPowerScore(pkm: Pkm): number {
  if (NonPkm.includes(pkm)) return 0
  return POWER_SCORE_BY_CATEGORY[getCategory(pkm)] ?? 1
}

export function getPowerEvaluation(powerScore: number, stage: number) {
  const powerAverage = POWER_AVERAGES[stage] ?? 40
  return clamp(50 + (100 * (powerScore - powerAverage)) / powerAverage, 0, 100)
}

export function getMaxItemComponents(stage: number): number {
  const nbComponentsPerStage = [
    0, // stage 0 (unused / pre-game)
    1, 2, 3, 5, 6, 6, 6, 7, 7, 8,       // stages 1–10
    9, 10, 10, 10, 11, 11, 12, 12, 12, 14, // stages 11–20
    14, 16, 16, 16, 18, 18, 20, 20, 22, 22 // stages 21–30
  ]
  return nbComponentsPerStage[stage] ?? 22
}

export function getNbComponentsOnBoard(board: IDetailledPokemon[]): number {
  return board
    .flatMap((pkm) => pkm.items)
    .reduce((total: number, item: Item) => {
      let nbComponents = 0
      if (Scarves.includes(item))
        nbComponents = item === Item.NULLIFY_BANDANNA ? 0 : 1
      else if (CraftableItems.includes(item)) nbComponents = 2
      else if (ItemComponentsNoScarf.includes(item)) nbComponents = 1
      return total + nbComponents
    }, 0)
}

export function getNbScarvesOnBoard(board: IDetailledPokemon[]): number {
  return board
    .flatMap((pkm) => pkm.items)
    .reduce((total: number, item: Item) => {
      let nbScarves = 0
      if (Scarves.includes(item))
        nbScarves = item === Item.NULLIFY_BANDANNA ? 2 : 1
      else if (item === Item.SILK_SCARF) nbScarves = 1
      return total + nbScarves
    }, 0)
}

export function getNbToolsOnBoard(board: IDetailledPokemon[]): number {
  return board.flatMap((pkm) => pkm.items).filter((item) => isIn(Tools, item))
    .length
}

export function rewriteBotRoundsRequiredto1(bot: IBot) {
  /*
  Compatibility layer for old bots that use roundsRequired > 1 to skip some stages
  We just rewrite everything with 1 stage = 1 step because it's just so easier to reason about
  */
  const oneSteps: IStep[] = []
  bot.steps.forEach((step) => {
    for (let i = 0; i < min(1)(step.roundsRequired); i++) {
      oneSteps.push({ board: step.board, roundsRequired: 1 })
    }
  })
  bot.steps = oneSteps.slice(0, MAX_BOTS_STAGE + 1)
  return bot
}

export function estimateElo(bot: IBot): number {
  const scores = bot.steps
    .map((step, stage) => getPowerEvaluation(getPowerScore(step.board), stage))
    .slice(3)

  const averageScore =
    scores.reduce((total, score) => total + score, 0) / scores.length

  if (averageScore < 10) return 500
  if (averageScore < 20) return 600
  if (averageScore < 30) return 700
  if (averageScore < 40) return 800
  if (averageScore < 50) return 900
  if (averageScore < 60) return 1000
  if (averageScore < 70) return 1100
  if (averageScore < 80) return 1200
  if (averageScore < 90) return 1300
  return 1400
}

export function validateBot(bot: IBot): string[] {
  const errors: string[] = []
  for (let stage = 0; stage < bot.steps.length; stage++) {
    try {
      const board = bot.steps[stage].board
      const synergies = computeSynergies(
        board.map((p) => {
          const pkm = PokemonFactory.createPokemonFromName(p.name, {
            emotion: p.emotion,
            shiny: p.shiny
          })
          pkm.positionX = p.x
          pkm.positionY = p.y
          p.items.forEach((item) => {
            pkm.items.add(item)
          })
          return pkm
        })
      )
      validateBoard(bot.steps[stage].board, stage, synergies)
    } catch (err) {
      errors.push(`Stage ${stage}: ${err}`)
    }
  }
  return errors
}

export function validateBoard(
  board: IDetailledPokemon[],
  stage: number,
  synergies: Map<Synergy, number>
): void {
  const team = board
    .filter((p) => p.y > 0)
    .map((p) => getPokemonData(p.name))
    .filter((p) => p.passive !== Passive.INANIMATE)
  const items = getNbComponentsOnBoard(board)
  const maxItems = getMaxItemComponents(stage)

  const scarves = getNbScarvesOnBoard(board)
  const maxScarves = getSynergyTier(synergies, Synergy.NORMAL)

  const nbToolsOnBoard = getNbToolsOnBoard(board)
  const nbMaxToolsOnBoard = getSynergyTier(synergies, Synergy.ARTIFICIAL)

  const duos = Object.values(PkmDuos)

  const numberOfGoldBows = board
    .flatMap((p) => p.items)
    .filter((item) => item === Item.GOLD_BOW).length
  const maxTeamSize = 9 + numberOfGoldBows

  function removeDuoPartner(p, index, arr) {
    const duo = duos.find((duo) => duo.includes(p.name))
    if (duo != null) {
      const partnerName = duo.find((x) => x !== p.name)
      const partner = arr.find((x) => x.name === partnerName)
      if (partner && arr.indexOf(partner) < index) {
        return false // duo partner already found in list, we remove the second one
      }
    }
    return true
  }

  function removeUniqueThatEvolveToLegendary(p) {
    if (
      getPokemonData(PkmFamily[p.name]).rarity === Rarity.UNIQUE &&
      getPokemonData(p.name).rarity === Rarity.LEGENDARY
    ) {
      return false // remove unique that evolves to legendary
    }
    return true
  }

  const uniques = team
    .filter((p) => p.rarity === Rarity.UNIQUE)
    .filter(removeDuoPartner)

  const legendaries = team
    .filter((p) => p.rarity === Rarity.LEGENDARY)
    .filter(removeDuoPartner)
    .filter(removeUniqueThatEvolveToLegendary)

  const additionalUncommon = team.filter(
    (p) => p.additional && p.rarity === Rarity.UNCOMMON
  )
  const additionalRare = team.filter(
    (p) => p.additional && p.rarity === Rarity.RARE
  )

  const additionalEpic = team.filter(
    (p) => p.additional && p.rarity === Rarity.EPIC
  )

  if (stage < PortalCarouselStages[1] && uniques.length > 0) {
    throw new Error(
      `Unique Pokemons can't be played before stage ${PortalCarouselStages[1]}`
    )
  }
  if (stage < PortalCarouselStages[2] && legendaries.length > 0) {
    throw new Error(
      `Legendary Pokemons can't be played before stage ${PortalCarouselStages[2]}`
    )
  }

  if (stage < AdditionalPicksStages[0] && additionalUncommon.length > 0) {
    throw new Error(
      `Uncommon additional picks can't be played before stage ${AdditionalPicksStages[0]}`
    )
  }
  if (stage < AdditionalPicksStages[1] && additionalRare.length > 0) {
    throw new Error(
      `Rare additional picks can't be played before stage ${AdditionalPicksStages[1]}`
    )
  }
  if (stage < AdditionalPicksStages[2] && additionalEpic.length > 0) {
    throw new Error(
      `Epic additional picks can't be played before stage ${AdditionalPicksStages[2]}`
    )
  }
  if (uniques.length > 1) {
    throw new Error(`Only one Unique Pokemon can be played`)
  }
  if (legendaries.length > 1) {
    throw new Error(`Only one Legendary Pokemon can be played`)
  }
  if (team.length > maxTeamSize) {
    throw new Error(`Too many Pokémon are being played in this team`)
  }
  if (items > maxItems) {
    throw new Error(`Too many item components are used at this stage`)
  }
  if (scarves > maxScarves) {
    throw new Error(`Too many silk scarves are used in this team`)
  }
  if (nbToolsOnBoard > nbMaxToolsOnBoard) {
    throw new Error(`Too many tools are used in this team`)
  }
}
